import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";

const areas = [
  "Bella Vista, AR",
  "Rogers, AR",
  "Bentonville, AR",
  "Fayetteville, AR",
  "Hindsville, AR",
  "Springdale, AR",
  "Centerton, AR",
  "Pea Ridge, AR",
  "Gravette, AR",
  "Siloam Springs, AR",
];

const ServiceAreas = () => {
  const ref = useRef(null);
  const mapRef = useRef<HTMLDivElement | null>(null);
  const [showMap, setShowMap] = useState(false);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (!mapRef.current || showMap) return;
    const el = mapRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setShowMap(true);
          io.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [showMap]);

  return (
    <section ref={ref} className="section-padding bg-charcoal text-primary-foreground">
      <div className="container-custom mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
            Serving <span className="text-accent-light">Northwest Arkansas</span>
          </h2>
          <p className="text-primary-foreground/70 text-lg max-w-2xl mx-auto">
            Professional landscape and irrigation services for residential and commercial properties across NWA and surrounding areas
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {areas.map((area, index) => (
            <motion.div
              key={area}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: index * 0.05 }}
              className="flex items-center gap-2 bg-charcoal-light/50 px-4 py-3 rounded-lg border border-border/20"
            >
              <MapPin className="h-4 w-4 text-accent-light flex-shrink-0" />
              <span className="text-sm font-medium">{area}</span>
            </motion.div>
          ))}
        </div>

        {/* Map Embed */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-12"
        >
          <div ref={mapRef} style={{ minHeight: 300, borderRadius: 8, overflow: "hidden", background: "hsl(var(--muted))" }}>
            {showMap && (
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d44972!2d-94.2218254!3d36.3165258!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87c91aad1203cd0b%3A0x8a3ee9b13cb6d490!2sLawnworks%20Of%20NWA!5e1!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Service area map for Northwest Arkansas landscaping"
              />
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServiceAreas;
